/* ⚠️ GENERATED from app/services/soundLibrary.ts by gen-twins.mjs — edit the TypeScript, not this. */
// services/soundLibrary.ts — the sounds a person can put under a clip: where
// they come from, which of them may be used, how they are credited, and how
// the mix is played back.
//
// 🟥 THE CATALOGUE IS JAMENDO, AND JAMENDO'S TERMS ARE TWO RULES, NOT ONE.
//    Every track is published under its OWN Creative Commons licence, and the
//    API itself is free "for non-commercial uses" only — commercial use of the
//    API is a separate agreement with Jamendo (licensing@jamendo.com). Code can
//    honour the first rule completely and the second not at all: a contract is
//    not a function. So this file does everything the code CAN do —
//      · never offer a track whose licence forbids the use a post makes of it,
//      · credit exactly the way the API terms demand (artist, Jamendo as the
//        provider, and a backlink to the track's page),
//      · record the licence on the post so the catalogue can be audited,
//      · keep the provider behind one interface so a licensed catalogue is a
//        one-module swap —
//    and says plainly, here, that the API agreement is still owed.
//
// 🟥 A POST IS A DERIVATIVE, AND ALLO IS A BUSINESS. So the only licences a
//    track may carry are the two that allow both: CC0 and CC BY. NonCommercial
//    (NC) forbids the platform; NoDerivatives (ND) forbids putting the song under
//    somebody's video at all; ShareAlike (SA) would oblige the user's video to be
//    released under the same licence, which nobody who posts a clip has agreed
//    to. The Jamendo API can filter all three server-side (ccnc/ccnd/ccsa) and
//    this module ALSO checks the URL that comes back, because a filter you did
//    not verify is a filter you are hoping about.
//
// 🟢 THIS FILE IS GENERATED INTO allo-web. One rule for which sounds exist and
//    how the mix plays, or the same clip sounds different on the two clients.

export const SOUND_PROVIDER = 'jamendo';
/** Jamendo's public client id — it travels in every browser request anyway. */
export const JAMENDO_CLIENT_ID = '97b4b9a8';
export const JAMENDO_API = 'https://api.jamendo.com/v3.0/tracks/';

/* ═══════════════════════════════════════════════════════════════════════════
   LICENCES
   ═══════════════════════════════════════════════════════════════════════════ */

const LICENSES = {
  'cc0': {
    code: 'cc0',
    label: 'CC0',
    commercial: true,
    derivatives: true,
    shareAlike: false,
    attribution: false
  },
  'by': {
    code: 'by',
    label: 'CC BY',
    commercial: true,
    derivatives: true,
    shareAlike: false,
    attribution: true
  },
  'by-sa': {
    code: 'by-sa',
    label: 'CC BY-SA',
    commercial: true,
    derivatives: true,
    shareAlike: true,
    attribution: true
  },
  'by-nc': {
    code: 'by-nc',
    label: 'CC BY-NC',
    commercial: false,
    derivatives: true,
    shareAlike: false,
    attribution: true
  },
  'by-nd': {
    code: 'by-nd',
    label: 'CC BY-ND',
    commercial: true,
    derivatives: false,
    shareAlike: false,
    attribution: true
  },
  'by-nc-sa': {
    code: 'by-nc-sa',
    label: 'CC BY-NC-SA',
    commercial: false,
    derivatives: true,
    shareAlike: true,
    attribution: true
  },
  'by-nc-nd': {
    code: 'by-nc-nd',
    label: 'CC BY-NC-ND',
    commercial: false,
    derivatives: false,
    shareAlike: false,
    attribution: true
  },
  'unknown': {
    code: 'unknown',
    label: 'Unknown',
    commercial: false,
    derivatives: false,
    shareAlike: false,
    attribution: true
  }
};

/** What a track's `license_ccurl` means. Anything unrecognised is 'unknown' — and unknown is not allowed. */
export function licenseFromUrl(url) {
  const u = String(url || '').toLowerCase();
  const out = code => ({
    ...LICENSES[code],
    url: url ? String(url) : null
  });
  if (!u) return out('unknown');
  if (/publicdomain\/zero/.test(u) || /\/cc0\b/.test(u)) return out('cc0');
  const m = u.match(/creativecommons\.org\/licenses\/(by(?:-nc)?(?:-sa|-nd)?)\//);
  if (!m) return out('unknown');
  const code = m[1];
  return LICENSES[code] ? out(code) : out('unknown');
}

/** The licences a sound under a post may carry. See the header for why only these two. */
export const ALLOWED_LICENSE_CODES = ['cc0', 'by'];
export function usableInPost(license) {
  if (!license) return false;
  return ALLOWED_LICENSE_CODES.includes(license.code) && license.commercial && license.derivatives && !license.shareAlike;
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE CATALOGUE
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * The Jamendo request for a page of usable tracks.
 * 🟢 THE FILTERS ARE IN THE QUERY. ccnc/ccnd/ccsa=false ask Jamendo to leave
 *    NC, ND and SA tracks out before they cross the wire; `usableInPost` then
 *    checks what actually arrived.
 */
export function jamendoTracksUrl(opts = {}) {
  const q = new URLSearchParams({
    client_id: opts.clientId || JAMENDO_CLIENT_ID,
    format: 'json',
    limit: String(Math.max(1, Math.min(200, Math.floor(opts.limit || 60)))),
    hasimage: 'true',
    imagesize: '200',
    audioformat: 'mp32',
    order: 'popularity_total',
    ccnc: 'false',
    ccnd: 'false',
    ccsa: 'false'
  });
  const search = String(opts.search || '').trim();
  if (search) q.set('namesearch', search);else if (opts.tag) q.set('fuzzytags', String(opts.tag));
  return `${JAMENDO_API}?${q.toString()}`;
}

/** One Jamendo result → a Sound, or null when it cannot be one. */
export function normalizeJamendo(raw) {
  if (!raw || !raw.id || !raw.audio) return null;
  return {
    id: String(raw.id),
    title: String(raw.name || '').trim() || 'Untitled',
    artist: String(raw.artist_name || '').trim() || 'Unknown artist',
    cover: raw.image || raw.album_image || null,
    url: String(raw.audio),
    duration: Math.max(0, Number(raw.duration) || 0),
    license: licenseFromUrl(raw.license_ccurl),
    page: raw.shareurl || raw.shorturl || null,
    provider: SOUND_PROVIDER
  };
}

/** A page of results → the sounds that may be offered. */
export function usableSounds(results) {
  const out = [];
  for (const r of Array.isArray(results) ? results : []) {
    const s = normalizeJamendo(r);
    if (s && usableInPost(s.license)) out.push(s);
  }
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE CREDIT
   ═══════════════════════════════════════════════════════════════════════════
   🟥 THE API TERMS NAME THREE THINGS: the artist as creator, Jamendo as the
      provider, and a backlink to the track's page. All three, everywhere the
      track is named — the picker row, the post's music pill, the reel. */
export const PROVIDER_LABEL = 'Jamendo';
export function attribution(track) {
  if (!track || !track.title) return null;
  const artist = String(track.artist || '').trim();
  return {
    /** "Title — Artist" */
    line: artist ? `${track.title} — ${artist}` : String(track.title),
    /** "via Jamendo · CC BY" */
    credit: `via ${PROVIDER_LABEL}${track.license && track.license.label ? ` · ${track.license.label}` : ''}`,
    page: track.page || null
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   THE MIX
   ═══════════════════════════════════════════════════════════════════════════ */

export const MIX_DEFAULT = {
  originalVolume: 100,
  addedSoundVolume: 100,
  isOriginalMuted: false,
  isAddedSoundMuted: false
};
const pct = v => Math.max(0, Math.min(100, Number(v))) / 100;
const num = v => Number.isFinite(Number(v)) ? Number(v) : 0;

/** What each element actually plays at, 0..1. One arithmetic for both players. */
export function effectiveVolumes(mix, listener = 1) {
  const m = {
    ...MIX_DEFAULT,
    ...(mix || {})
  };
  const L = Math.max(0, Math.min(1, Number(listener) || 0));
  return {
    original: m.isOriginalMuted ? 0 : (Number.isFinite(Number(m.originalVolume)) ? pct(m.originalVolume) : 1) * L,
    added: m.isAddedSoundMuted ? 0 : (Number.isFinite(Number(m.addedSoundVolume)) ? pct(m.addedSoundVolume) : 1) * L
  };
}

/**
 * Where the song should be, given where the video is.
 *
 * 🟢 THE SONG STARTS WHERE THE AUTHOR PUT IT, and loops over the part of it
 *    that is left. TikTok's picker lets you drag the window along the track;
 *    `startMs` is that window's left edge, and the maths here is what makes
 *    the same clip sound the same on the phone, in the browser, and in the
 *    editor that chose it.
 */
export function musicPositionFor(videoMs, musicDurMs, startMs = 0) {
  const v = Math.max(0, num(videoMs));
  const d = Math.max(0, num(musicDurMs));
  /* 🟢 an unknown length cannot pin the start — the metadata simply has not
     arrived yet, and the song still begins where the author put it */
  if (d <= 0) return Math.max(0, num(startMs)) + v;
  const s = Math.max(0, Math.min(num(startMs), Math.max(0, d - 1)));
  const span = d - s;
  if (span <= 0) return s;
  return s + v % span;
}

/** The furthest the start may sit, so the window still covers the clip. */
export function maxStartMs(musicDurMs, videoDurMs) {
  const d = Math.max(0, num(musicDurMs));
  const v = Math.max(0, num(videoDurMs));
  if (d <= 0) return 0;
  return Math.max(0, d - Math.min(d, v > 0 ? v : d));
}
export function clampStartMs(startMs, musicDurMs, videoDurMs) {
  return Math.max(0, Math.min(num(startMs), maxStartMs(musicDurMs, videoDurMs)));
}

/** "m:ss" */
export function fmtDuration(seconds) {
  const s = Math.max(0, Math.floor(num(seconds)));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   WHAT THE POST CARRIES
   ═══════════════════════════════════════════════════════════════════════════
   🟥 THE NAMES ARE THE PHONE'S NAMES — audioTrackUrl, isAddedSoundMuted and so
      on are read by every player already shipped. New facts get new fields
      beside them; nothing is renamed. */
/* 🎵 🟥 A SONG NOBODY HEARS IS NOT THE POST'S SONG. The phone's editor suggests a
   track for a fresh video and holds it MUTED until it is tapped — and an untapped
   suggestion was written onto the post as if chosen: the feed named a song the
   clip did not play. A track counts only when it is audible. */
export function soundCredited(track, mix) {
  const m = {
    ...MIX_DEFAULT,
    ...(mix || {})
  };
  return !!(track && track.url && !m.isAddedSoundMuted);
}

/** Should this POST name a song? — only one it actually plays. Heals posts
    already written with a muted suggestion, on both clients. */
export function showsSoundCredit(post) {
  return !!(post && post.audioTrackTitle && post.isAddedSoundMuted !== true);
}
export function soundFieldsFor(track, mix, startMs = 0) {
  const m = {
    ...MIX_DEFAULT,
    ...(mix || {})
  };
  if (!soundCredited(track, m)) {
    return {
      audioTrackUrl: null,
      audioTrackTitle: null,
      audioTrackArtist: null,
      audioTrackLicense: null,
      audioTrackPage: null,
      audioTrackProvider: null,
      audioStartMs: 0,
      isOriginalMuted: !!m.isOriginalMuted,
      isAddedSoundMuted: true,
      originalVolume: Math.round(pct(m.originalVolume) * 100),
      addedSoundVolume: Math.round(pct(m.addedSoundVolume) * 100)
    };
  }
  return {
    audioTrackUrl: track.url,
    audioTrackTitle: track.title,
    audioTrackArtist: track.artist || null,
    audioTrackLicense: track.license ? track.license.code : null,
    audioTrackPage: track.page || null,
    audioTrackProvider: track.provider || SOUND_PROVIDER,
    audioStartMs: Math.max(0, Math.round(num(startMs))),
    isOriginalMuted: !!m.isOriginalMuted,
    isAddedSoundMuted: !!m.isAddedSoundMuted,
    originalVolume: Math.round(pct(m.originalVolume) * 100),
    addedSoundVolume: Math.round(pct(m.addedSoundVolume) * 100)
  };
}

/** The picker's shelves — one list, so the two clients offer the same. */
export const SOUND_CATEGORIES = [{
  key: 'trending',
  label: 'Trending',
  tag: null
}, {
  key: 'pop',
  label: 'Pop',
  tag: 'pop'
}, {
  key: 'arabic',
  label: 'Arabic',
  tag: 'arabic'
}, {
  key: 'oriental',
  label: 'Oriental',
  tag: 'oriental'
}, {
  key: 'persian',
  label: 'Persian',
  tag: 'persian'
}, {
  key: 'turkish',
  label: 'Turkish',
  tag: 'turkish'
}, {
  key: 'hiphop',
  label: 'Hip-Hop',
  tag: 'hiphop'
}, {
  key: 'rnb',
  label: 'R&B',
  tag: 'rnb'
}, {
  key: 'electronic',
  label: 'Electronic',
  tag: 'electronic'
}, {
  key: 'chill',
  label: 'Chill',
  tag: 'lounge'
}, {
  key: 'rock',
  label: 'Rock',
  tag: 'rock'
}, {
  key: 'cinematic',
  label: 'Cinematic',
  tag: 'soundtrack'
}, {
  key: 'sad',
  label: 'Sad',
  tag: 'sad'
}];
